import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  GSSidePanelPage,
  GSActionBar,
  GSEmptyList,
  GSFormSection,
  GSInput,
  GSItemList,
  defaultGapping,
  defaultLayouts,
  defaultPadding,
  defaultTypography,
  golfstatusColors,
} from "golfstatus_react_components";
import {
  faChevronLeft,
  faFlag,
  faPlus,
  faSearch,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";
import { useInlineResponseEditing } from "../../../hooks/customFieldResponseHooks";
import {
  slideoutContentStyle,
  slideoutPageStyle,
  slideoutRootStyle,
} from "../../../templates/slideout-styles";
import {
  getRecordCustomFieldResponses,
  removeRecordForm,
  clearCurrentRecordResponses,
  clearNotifications,
  selectCurrentRecordResponses,
  selectCustomFieldResponsesLoading,
} from "../../../reducers/customFieldResponsesSlice";
import {
  getRecordResponseGroup,
  filterResponseNodes,
  isFormOrderTied,
} from "../../../helpers/CustomFieldResponses";
import TournamentOrderFormResponseCard from "./tournament-order-form-response-card";

const responseableRemoveTypes = {
  Player: "players",
  Team: "tournament-teams",
  TournamentTeam: "tournament-teams",
  Sponsor: "sponsors",
};

const addFormLabels = {
  player: "Add Player Form",
  team: "Add Team Form",
  sponsor: "Add Sponsor Form",
};

const recordViewActions = {
  "sponsors": { title: "View Sponsor", buttonIcon: faFlag, base: "sponsors" },
  "tournament-teams": { title: "View Team", buttonIcon: faUsers, base: "teams" },
};

const getFormAttachedAt = (formEntry) => {
  let earliest = "";
  (formEntry?.questions ?? []).forEach((question) => {
    (question?.responses ?? []).forEach((answer) => {
      const at = answer?.response?.createdAt;
      if (at && (earliest === "" || at < earliest)) {
        earliest = at;
      }
    });
  });
  return earliest;
};

const sectionStyle = {
  ...defaultLayouts.vStack,
  ...defaultLayouts.fullWidth,
  ...defaultGapping.mediumGap,
  ...defaultPadding.xLargePad.apply("horizontal"),
  ...defaultPadding.xLargePad.apply("vertical"),
  borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
};

const bodySectionStyle = {
  ...defaultLayouts.vStack,
  ...defaultLayouts.fullWidth,
};

const emptySectionStyle = {
  ...defaultPadding.largeLayoutPad.apply("vertical"),
  ...defaultPadding.xLargePad.apply("horizontal"),
};

const searchSectionStyle = {
  ...sectionStyle,
  position: "sticky",
  top: 0,
  zIndex: 1,
  background: golfstatusColors.white,
};


const packageLabelStyle = {
  ...defaultTypography.overline2,
  ...defaultTypography.uppercase,
  color: golfstatusColors.grey500,
};

const cardWrapperStyle = {
  ...defaultLayouts.fullWidth,
};

const sectionContentStyle = {
  ...defaultLayouts.vStack,
  ...defaultLayouts.fullWidth,
  ...defaultGapping.mediumLargeGap,
};

const RecordFormResponsesPage = ({
  tournamentID,
  recordType,
  recordKey,
  viewTeamID,
  emptyEntity,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const fetchResponses = useCallback(() => {
    if (recordType && recordKey) {
      dispatch(getRecordCustomFieldResponses({ recordType, key: recordKey }));
    }
  }, [dispatch, recordType, recordKey]);

  useEffect(() => {
    fetchResponses();
    return () => {
      dispatch(clearCurrentRecordResponses());
      dispatch(clearNotifications());
    };
  }, [dispatch, fetchResponses]);

  const payload = useSelector(selectCurrentRecordResponses);
  const loading = useSelector(selectCustomFieldResponsesLoading);

  const [search, setSearch] = useState("");
  const {
    banner,
    editingKey,
    requestEdit,
    commitRef,
    setPendingEdit,
    leftNavigation,
  } = useInlineResponseEditing(() => navigate(-1));

  const baseGroup = getRecordResponseGroup(payload);
  const hasResponses = (baseGroup?.forms ?? []).some(
    (entry) => (entry?.questions?.length ?? 0) > 0
  );
  const responseGroup = baseGroup
    ? filterResponseNodes([baseGroup], { search })[0]
    : null;
  const forms = [...(responseGroup?.forms ?? [])].sort((a, b) =>
    `${getFormAttachedAt(b)}`.localeCompare(`${getFormAttachedAt(a)}`)
  );
  const packageForms = forms.filter((entry) => isFormOrderTied(entry));
  const manualForms = forms.filter((entry) => !isFormOrderTied(entry));
  const packageName = responseGroup?.package?.name ?? payload?.package?.name;
  const recordName = payload?.record?.name;
  const pageTitle = recordName
    ? `${recordName} Form Responses`
    : "Form Responses";
  const orderKey = payload?.record?.registrationOrderKey;
  const hasOrderTiedForms = (baseGroup?.forms ?? []).some(isFormOrderTied);
  const getViewAction = () => {
    if (viewTeamID) {
      return {
        title: "View Team",
        buttonIcon: faUsers,
        onClick: () => navigate(`/${tournamentID}/teams/${viewTeamID}`),
      };
    }
    const config = recordViewActions[recordType];
    if (config && recordKey) {
      return {
        title: config.title,
        buttonIcon: config.buttonIcon,
        onClick: () => navigate(`/${tournamentID}/${config.base}/${recordKey}`),
      };
    }
    return null;
  };
  const viewAction = getViewAction();

  const handleRemoveForm = async (entry) => {
    const responseable = payload?.record;
    const removeType = responseableRemoveTypes[responseable?.type];
    if (!removeType || !entry?.form?.fieldSetKey) {
      return;
    }
    const result = await dispatch(
      removeRecordForm({
        recordType: removeType,
        key: responseable?.id,
        fieldSetKey: entry?.form?.fieldSetKey,
      })
    );
    if (result?.meta?.requestStatus === "fulfilled") {
      fetchResponses();
    }
  };

  const getNavigation = () => ({
    title: "Form Responses",
    leftIcon: faChevronLeft,
    leftButtonClick: leftNavigation,
  });

  const addFormLabel = addFormLabels[emptyEntity] ?? "Add Form";

  const getHeaderActions = () => {
    const actions = [
      {
        buttonTitle: addFormLabel,
        actionIcon: faPlus,
        type: "black no-wrap mobile-icon",
        actionClick: () => navigate("forms"),
      },
    ];
    if (orderKey && hasOrderTiedForms) {
      actions.push({
        buttonTitle: "View All",
        type: "light-grey no-wrap",
        actionClick: () =>
          navigate(`/${tournamentID}/reporting/${orderKey}/form-responses`),
      });
    }
    return actions;
  };

  const getHeaderSection = () => (
    <div style={sectionStyle}>
      <GSActionBar
        header={<div style={{ ...defaultTypography.headline3 }}>{pageTitle}</div>}
        pageActions={getHeaderActions()}
      ></GSActionBar>
    </div>
  );

  const getEmptyDetail = () => {
    if (hasResponses) {
      return "Try changing the search term.";
    }
    const subject = recordName || `This ${emptyEntity}`;
    return `${subject} has not responded to any form questions.`;
  };

  const getSearchSection = () => (
    <div style={searchSectionStyle}>
      <GSInput
        textValue={search}
        placeholder="Search..."
        leftIcon={faSearch}
        onChange={(event) => setSearch(event.target.value)}
      />
    </div>
  );

  const getCardList = (sectionForms) => (
    <GSItemList
      type="vertical x-large-gap"
      style={{ ...defaultLayouts.fullWidth }}
      items={sectionForms}
      listItem={(entry) => (
        <div style={cardWrapperStyle} key={entry?.form?.id}>
          <TournamentOrderFormResponseCard
            form={entry}
            viewAction={viewAction}
            onRemove={entry?.form?.fieldSetKey ? handleRemoveForm : undefined}
            isRemoveDisabled={isFormOrderTied(entry)}
            onSaved={fetchResponses}
            onUnsaved={setPendingEdit}
            editingKey={editingKey}
            onEdit={requestEdit}
            commitRef={commitRef}
          />
        </div>
      )}
    ></GSItemList>
  );

  const getFormSection = (sectionForms, label, key) => (
    <GSFormSection
      key={key}
      extras={
        <div style={sectionContentStyle}>
          {label && <div style={packageLabelStyle}>{label}</div>}
          {getCardList(sectionForms)}
        </div>
      }
    ></GSFormSection>
  );

  const getEmptyState = () => (
    <div style={bodySectionStyle}>
      <GSEmptyList
        style={emptySectionStyle}
        title={
          hasResponses
            ? "We couldn't find the response you're looking for..."
            : "No Form Responses"
        }
        detail={getEmptyDetail()}
        actions={
          hasResponses
            ? [{ title: "Clear Search", type: "grey", onClick: () => setSearch("") }]
            : [
                {
                  title: addFormLabel,
                  buttonIcon: faPlus,
                  type: "black",
                  onClick: () => navigate("forms"),
                },
              ]
        }
      />
    </div>
  );

  const getBodySection = () => {
    if (forms.length === 0) {
      return getEmptyState();
    }
    return (
      <div style={bodySectionStyle}>
        {manualForms.length > 0 && getFormSection(manualForms, null, "manual")}
        {packageForms.length > 0 &&
          getFormSection(packageForms, packageName, "package")}
      </div>
    );
  };

  const getContent = () => (
    <div style={slideoutContentStyle}>
      {getHeaderSection()}
      {getSearchSection()}
      {getBodySection()}
    </div>
  );

  return (
    <tournament-record-form-responses style={slideoutRootStyle}>
      <GSSidePanelPage
        header={getNavigation()}
        loading={
          loading?.includes?.("getRecordCustomFieldResponses") &&
          !payload?.record
        }
        banner={banner}
        content={getContent()}
        style={slideoutPageStyle}
      ></GSSidePanelPage>
    </tournament-record-form-responses>
  );
};

export default RecordFormResponsesPage;
